<script setup lang="ts">
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiSettingRow from '@/components/pos-v2/ui/PosUiSettingRow.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import V2ReservationsNotice from '../V2ReservationsNotice.vue';
  import type { IReservationSettings } from '@/interfaces/IReservationSettings';
  import { roundPrice } from '@/utils';

  const props = defineProps<{
    value: IReservationSettings;
    /** Deposits exist for the restaurant's country. */
    available: boolean;
    mpConnected: boolean;
    currency: string;
    paymentsUrl: string;
  }>();
  const emit = defineEmits<{
    (event: 'input', value: IReservationSettings): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const amountText = ref(String(props.value.depositAmountPerPerson || ''));
  const modes = computed(() =>
    ['none', 'threshold', 'always'].map((id) => ({
      id,
      label: String(proxy.$t(`reservations.depositSettings.mode.${id}`)),
    }))
  );
  const amountError = computed(() =>
    props.value.depositMode !== 'none' &&
    (!Number.isFinite(props.value.depositAmountPerPerson) ||
      props.value.depositAmountPerPerson <= 0)
      ? String(proxy.$t('reservations.depositSettings.amountRequired'))
      : ''
  );

  watch(
    () => props.value.depositAmountPerPerson,
    (amount) => {
      if (Number(amountText.value.replace(',', '.')) !== amount)
        amountText.value = String(amount || '');
    }
  );
  function update<K extends keyof IReservationSettings>(
    key: K,
    value: IReservationSettings[K]
  ) {
    if (props.mpConnected) emit('input', { ...props.value, [key]: value });
  }
  function modeChanged(value: string) {
    if (value === 'none' || value === 'threshold' || value === 'always')
      update('depositMode', value);
  }
  function amountChanged(value: string) {
    amountText.value = value;
    const amount = Number(value.replace(',', '.'));
    update(
      'depositAmountPerPerson',
      Number.isFinite(amount) ? roundPrice(amount) : 0
    );
  }
</script>

<template>
  <PosUiSettingsCard
    :class="{ 'deposit-settings': available }"
    flush
    :title="$t('reservations.settings.nav.deposit')"
    :description="
      available ? $t('reservations.depositSettings.description') : undefined
    "
  >
    <PosUiEmptyState
      v-if="!available"
      class="deposit-unavailable"
      icon="credit-card-off-outline"
      :title="$t('reservations.depositSettings.unavailableTitle')"
      :description="$t('reservations.depositSettings.unavailableDescription')"
    />
    <template v-else>
      <div v-if="!mpConnected" class="card-block">
        <V2ReservationsNotice
          icon="credit-card-outline"
          :text="
            $t(
              value.depositMode !== 'none'
                ? 'reservations.deposit.pausedNoMp'
                : 'reservations.depositSettings.needsMp'
            )
          "
          :link="{
            to: paymentsUrl,
            label: $t('reservations.depositSettings.connectMp'),
          }"
        />
      </div>
      <fieldset :disabled="!mpConnected" class="deposit-fields">
        <legend class="visually-hidden">
          {{ $t('reservations.settings.nav.deposit') }}
        </legend>
        <PosUiSettingRow wide :label="$t('reservations.depositSettings.when')">
          <div class="mode-options">
            <PosUiSegmented
              :value="value.depositMode"
              :items="modes"
              :aria-label="$t('reservations.depositSettings.when')"
              @input="modeChanged"
            />
          </div>
        </PosUiSettingRow>
        <PosUiSettingRow
          v-show="value.depositMode === 'threshold'"
          :label="
            $t('reservations.depositSettings.threshold', {
              count: value.depositPartyThreshold,
            })
          "
        >
          <PosUiStepper
            :value="value.depositPartyThreshold"
            :min="1"
            :max="200"
            :disabled="!mpConnected"
            :aria-label="
              $t('reservations.depositSettings.threshold', {
                count: value.depositPartyThreshold,
              })
            "
            @input="update('depositPartyThreshold', $event)"
          />
        </PosUiSettingRow>
        <PosUiSettingRow
          :label="$t('reservations.depositSettings.amount')"
          :required="value.depositMode !== 'none'"
          :error="amountError"
          input-id="rsv-deposit-amount"
        >
          <PosUiInput
            id="rsv-deposit-amount"
            inputmode="decimal"
            :value="amountText"
            :prefix="currency"
            :disabled="!mpConnected || value.depositMode === 'none'"
            @input="amountChanged"
          />
        </PosUiSettingRow>
        <PosUiSettingRow
          :label="$t('reservations.depositSettings.refundHours')"
          :hint="
            $t('reservations.depositSettings.hoursBefore', {
              count: value.depositRefundHours,
            })
          "
        >
          <PosUiStepper
            :value="value.depositRefundHours"
            :min="0"
            :max="32767"
            :disabled="!mpConnected || value.depositMode === 'none'"
            :aria-label="$t('reservations.depositSettings.refundHours')"
            @input="update('depositRefundHours', $event)"
          />
        </PosUiSettingRow>
      </fieldset>
    </template>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .deposit-settings {
    .card-block {
      @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-4 md:tw-px-6;
      .reservations-notice {
        @apply tw-mb-0;
      }
    }
    // Controls draw their own disabled state; only the text is dimmed here,
    // so the two opacities never stack into unreadable labels.
    .deposit-fields {
      @apply tw-m-0 tw-min-w-0 tw-border-0 tw-p-0;
      &:disabled :deep(.row-label),
      &:disabled :deep(.row-hint) {
        @apply tw-text-neutral-400;
      }
    }
    .mode-options {
      @apply tw-min-w-0 tw-max-w-full;
    }
    .visually-hidden {
      @apply tw-sr-only;
    }
    :deep(.pos-ui-segmented-option) {
      @apply tw-min-h-11 tw-h-auto tw-whitespace-normal tw-px-3 tw-py-2;
    }
  }
</style>
